import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useInterval } from '../../hooks/use-interval';

import { toFixedNumber } from '../../services/number-formatting-service';

const roundWeight = (weight) => toFixedNumber(weight, 2);

const AnimatedWeightDisplay = ({ timeMs = 300, ticks = 10, endWeight, children }) => {
  const [weight, setWeight] = useState(endWeight);
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const startWeight = useMemo(() => weight, [endWeight]);
  const clearIntervalRef = useRef();
  const weightTickDelta = (endWeight - startWeight) / ticks;
  const interval = timeMs / ticks;

  const clearTimer = useCallback(() => {
    if (!clearIntervalRef.current) {
      return;
    }
    clearIntervalRef.current();
    clearIntervalRef.current = undefined;
  }, [clearIntervalRef]);

  useEffect(clearTimer, [endWeight, clearTimer]);

  const updateWeightOnTick = useCallback(() => {
    if (Math.abs(endWeight - weight) < Math.abs(weightTickDelta)) {
      setWeight(roundWeight(endWeight));
      clearTimer();
      return;
    }

    setWeight(roundWeight(weight + weightTickDelta));
  }, [weight, setWeight, clearTimer, endWeight, weightTickDelta]);

  clearIntervalRef.current = useInterval(updateWeightOnTick, interval);

  return children(weight);
};

AnimatedWeightDisplay.propTypes = {
  endWeight: PropTypes.number.isRequired,
  timeMs: PropTypes.number,
  ticks: PropTypes.number,
  children: PropTypes.func.isRequired,
};

export default AnimatedWeightDisplay;
